import { useEffect, useState, type FormEvent } from "react";
import { boardApi, epicsApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import type { Epic } from "../api/types";
import { useTeams } from "../hooks/useTeams";
import { TeamPicker } from "../components/TeamPicker";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { useConfirm } from "../components/ConfirmDialog";
import { formatDate } from "../utils/time";

export function EpicsPage() {
  const { teams, error: teamsError, selectedId, select } = useTeams();
  const [epics, setEpics] = useState<Epic[] | null>(null);
  const [ticketCounts, setTicketCounts] = useState<Record<string, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  const confirm = useConfirm();

  const load = async (teamId: string) => {
    setError(null);
    setEpics(null);
    setTicketCounts(null);
    try {
      const list = await epicsApi.listByTeam(teamId);
      setEpics(list);
      boardApi
        .get(teamId)
        .then((board) => {
          const counts: Record<string, number> = {};
          board.columns.forEach((col) =>
            col.tickets.forEach((t) => {
              if (t.epicId) counts[t.epicId] = (counts[t.epicId] ?? 0) + 1;
            }),
          );
          setTicketCounts(counts);
        })
        .catch(() => setTicketCounts({}));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load epics.");
    }
  };

  useEffect(() => {
    if (selectedId) void load(selectedId);
  }, [selectedId]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedId || !newTitle.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await epicsApi.create(selectedId, newTitle.trim());
      setNewTitle("");
      setCreating(false);
      await load(selectedId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create epic.");
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing?.title.trim() || !selectedId) return;
    setBusy(true);
    setError(null);
    try {
      await epicsApi.rename(editing.id, editing.title.trim());
      setEditing(null);
      await load(selectedId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to rename epic.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (epic: Epic) => {
    if (!selectedId) return;
    const ok = await confirm({
      title: "Delete epic",
      message: `Delete epic “${epic.title}”? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await epicsApi.remove(epic.id);
      await load(selectedId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete epic.");
    }
  };

  if (teams === null) return <Loading />;
  if (teamsError) return <ErrorBanner message={teamsError} />;
  if (teams.length === 0)
    return <EmptyState message="Create a team first, then add epics to it." />;

  return (
    <section className="page">
      <div className="page-header">
        <div className="stack" style={{ gap: 12 }}>
          <h1 className="page-title">Epics</h1>
          <TeamPicker teams={teams} selectedId={selectedId} onChange={select} />
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)} disabled={!selectedId}>
          <Icon name="plus" size={18} /> Create epic
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {epics === null ? (
        <Loading />
      ) : epics.length === 0 ? (
        <EmptyState message="No epics for this team yet." />
      ) : (
        <div className="card table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th className="num">Tickets</th>
                <th>Modified</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {epics.map((epic) => {
                const count = ticketCounts?.[epic.id] ?? 0;
                const known = ticketCounts !== null;
                const canDelete = known && count === 0;
                return (
                  <tr key={epic.id}>
                    <td className="cell-main" data-label="Title">
                      <div className="cell-title">{epic.title}</div>
                    </td>
                    <td className="num" data-label="Tickets">
                      {!known ? "…" : count}
                    </td>
                    <td data-label="Modified">{formatDate(epic.modifiedAt)}</td>
                    <td data-label="Actions">
                      <div className="table-actions">
                        <button
                          className="secondary btn-sm"
                          onClick={() => setEditing({ id: epic.id, title: epic.title })}
                        >
                          <Icon name="edit" size={15} /> Edit
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => remove(epic)}
                          disabled={!canDelete}
                          title={count > 0 ? "Referenced by tickets — cannot delete" : "Delete epic"}
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="table-note">Delete is disabled while tickets reference the epic.</p>
        </div>
      )}

      {creating && (
        <Modal title="Create epic" onClose={() => setCreating(false)}>
          <form className="stack" onSubmit={create}>
            <label className="field">
              <span className="field-label">Title</span>
              <input
                autoFocus
                placeholder="e.g. Checkout reliability"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </label>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={busy || !newTitle.trim()}>
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title="Edit epic" onClose={() => setEditing(null)}>
          <form className="stack" onSubmit={saveRename}>
            <label className="field">
              <span className="field-label">Title</span>
              <input
                autoFocus
                value={editing.title}
                onChange={(e) => setEditing({ id: editing.id, title: e.target.value })}
              />
            </label>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={busy || !editing.title.trim()}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
